import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import SortableTable from '../../../components/ui/SortableTable';
import IssuanceForm from '../forms/IssuanceForm';
import ApiService from '../../../services/apiService';
import { supabase } from '../../../config';
import formatDate from '../utils/dateFormatter';
import formatNumberWithUnits from '../utils/numberFormatter';
import formatCurrencyWithUnits from '../utils/currencyFormatter';

const IssuancesPage = ({ issuances, shareholders, shareClasses, onDataRefresh, addMessage, selectedCompany, userRole }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [globalShareTypes, setGlobalShareTypes] = useState([]);

    // Fetch global share types
    useEffect(() => {
        const fetchGlobalShareTypes = async () => {
            try {
                console.log('Fetching global share types for IssuancesPage...');
                
                // First try: get where company_id IS NULL
                const { data, error } = await supabase
                    .from('equity_share_classes')
                    .select('*')
                    .is('company_id', null)
                    .order('priority', { ascending: true });

                console.log('Global share types result:', { data, error });

                if (!error && data) {
                    setGlobalShareTypes(data);
                    console.log(`✅ Loaded ${data.length} global share types for issuances table`);
                } else if (error) {
                    console.error('RLS policy might be blocking. Trying fallback...');
                    
                    // Fallback: try getting ALL share classes and filter client-side
                    const { data: allData, error: allError } = await supabase
                        .from('equity_share_classes')
                        .select('*')
                        .order('priority', { ascending: true });
                    
                    if (!allError && allData) {
                        const globalTypes = allData.filter(sc => sc.company_id === null);
                        setGlobalShareTypes(globalTypes);
                        console.log(`✅ Fallback: Loaded ${globalTypes.length} global share types`);
                    }
                }
            } catch (error) {
                console.error('Error fetching global share types:', error);
            }
        };

        fetchGlobalShareTypes();
    }, []);

    const handleCreateIssuances = async (issuanceData) => {
        if (!selectedCompany) {
            addMessage("No company selected.", 'error');
            return;
        }
        try {
            await ApiService.createIssuance({ 
                ...issuanceData, 
                company_id: selectedCompany.id 
            });
            onDataRefresh();
            setShowCreateModal(false);
            addMessage('Share issuance created successfully!', 'success');
        } catch (error) {
            addMessage(`Error creating issuance: ${error.message}`, 'error');
        }
    };

    const handleDeleteClick = (issuance) => {
        setItemToDelete(issuance);
        setShowConfirmDelete(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await ApiService.deleteIssuance(itemToDelete.id);
            onDataRefresh();
            setShowConfirmDelete(false);
            setItemToDelete(null);
            addMessage('Share issuance deleted successfully!', 'success');
        } catch (error) {
            addMessage(`Error deleting issuance: ${error.message}`, 'error');
        }
    };

    const getShareholderName = (shareholderId) => {
        const shareholder = shareholders.find(s => s.id === shareholderId);
        if (!shareholder) return 'Unknown';
        if (shareholder.entity_type === 'individual' || shareholder.entity_type === 'Individual') {
            return `${shareholder.first_name || ''} ${shareholder.last_name || ''}`.trim() || 'N/A';
        } else {
            return shareholder.name || 'N/A';
        }
    };

    const getShareClassName = (shareClassId) => {
        // Look in both company-specific classes and global templates
        const allShareClasses = [...(shareClasses || []), ...globalShareTypes];
        const shareClass = allShareClasses.find(sc => sc.id === shareClassId);
        return shareClass ? shareClass.name : 'Unknown';
    };

    const issuancesTableColumns = [
        { 
            key: 'shareholder_name', 
            header: 'Shareholder', 
            isSortable: true,
            render: (row) => getShareholderName(row.shareholder_id)
        },
        { 
            key: 'share_class_name', 
            header: 'Share Class', 
            isSortable: true,
            render: (row) => getShareClassName(row.share_class_id)
        },
        { 
            key: 'shares', 
            header: 'No. of Shares', 
            isSortable: true,
            isSummable: true,
            render: (row) => formatNumberWithUnits(row.shares || 0)
        },
        { 
            key: 'price_per_share', 
            header: 'Price/Share', 
            isSortable: true,
            render: (row) => row.price_per_share ? formatCurrencyWithUnits(parseFloat(row.price_per_share), selectedCompany?.currency || 'USD') : 'N/A'
        },
        { 
            key: 'total_value', 
            header: 'Issued Capital', 
            isSortable: true,
            isSummable: true,
            render: (row) => {
                const total = (row.shares || 0) * (row.price_per_share || 0);
                return total > 0 ? formatCurrencyWithUnits(total, selectedCompany?.currency || 'USD') : 'N/A';
            }
        },
        { 
            key: 'issue_date', 
            header: 'Issue Date', 
            isSortable: true, 
            type: 'date',
            render: (row) => formatDate(row.issue_date)
        },
        { 
            key: 'round', 
            header: 'Round', 
            isSortable: true,
            render: (row) => row.round || 'N/A'
        },
        { 
            key: 'payment_status', 
            header: 'Payment Status', 
            isSortable: true,
            render: (row) => (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                    row.payment_status === 'Yes' 
                        ? 'bg-green-100 text-green-800' 
                        : row.payment_status === 'Partial' 
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {row.payment_status || 'No'}
                </span>
            )
        },
        { 
            key: 'actions', 
            header: 'Actions', 
            isSortable: false,
            render: (row) => (
                userRole !== 'viewer' && (
                    <button
                        onClick={() => handleDeleteClick(row)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                )
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Share Issuances</h2>
                {userRole !== 'viewer' && (
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <PlusCircle className="h-5 w-5 mr-2" />
                        Add Share Issuance
                    </button>
                )}
            </div>

            {!selectedCompany ? (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    Please select a company to view share issuances.
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600">Total Shares Issued</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {formatNumberWithUnits(issuances.reduce((sum, iss) => sum + (iss.shares || 0), 0))}
                            </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600">Total Issued Capital</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {formatCurrencyWithUnits(issuances.reduce((sum, iss) => sum + ((iss.shares || 0) * (iss.price_per_share || 0)), 0), selectedCompany?.currency || 'USD')}
                            </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600">Total Issuances</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {issuances.length}
                            </p>
                        </div>
                    </div>

                    {/* Issuances Table */}
                    <SortableTable
                        key={`issuances-${globalShareTypes.length}`}
                        data={issuances}
                        columns={issuancesTableColumns}
                        entityType="issuance"
                        addMessage={addMessage}
                    />
                </>
            )}

            {showCreateModal && (
                <Modal onClose={() => setShowCreateModal(false)} title="Add Share Issuance" maxWidth="max-w-5xl">
                    <IssuanceForm
                        shareholders={shareholders}
                        shareClasses={shareClasses}
                        onSubmit={handleCreateIssuances}
                        onCancel={() => setShowCreateModal(false)}
                        addMessage={addMessage}
                        selectedCompany={selectedCompany}
                    />
                </Modal>
            )}

            {showConfirmDelete && (
                <Modal onClose={() => setShowConfirmDelete(false)} title="Confirm Delete">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Are you sure you want to delete this share issuance? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                type="button"
                                onClick={() => setShowConfirmDelete(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                            >
                                Confirm Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default IssuancesPage;